import {observer} from 'mobx-react-lite'
import React, {useEffect} from 'react'
import tw from 'tailwind-styled-components'
import {Lang} from 'yougile-lang'
import {Hub} from 'yougile-model'
import {SubscribersMenu} from '@components/chat/chat-subscribers-chooser/chat-subscribers-chooser-menu'
import {
  ChatSubscribersChooserStore,
  StoreContext,
  useStore
} from '@components/chat/chat-subscribers-chooser/chat-subscribers-chooser-store'
import {ChatSubscribersService} from '@components/chat/chat-subscribers-chooser/chat-subscribers-service'
import {AvatarGroup} from '@components/common/base/avatar'
import {IconButton, LinkButton} from '@components/common/base/buttons'
import {useMemoOne} from '@containers/hooks'
import {withUpdaters} from '@containers/updaters'
import {SyncUpdater} from '@containers/updaters/sync-updater'
import {globals} from '@extensions/globals-base'

interface ScopeProps {
  chat?: Hub
  canEditSubscribers: 'yes' | 'no' | 'self'
}

const TwTitle = tw.div`
  text-12
  leading-4
  whitespace-nowrap
  after:content-[':']
`

const TwSubscribedUsers = tw.div<{$clickable?: boolean}>`
  ${props => props.$clickable && 'cursor-pointer'}
  flex
  items-center
  gap-8
`

const TwSubscribed = tw.div`
  flex-none
  cursor-default
  h-48
  flex-shrink-0
  flex-grow-0
  basis-48
  px-16
  relative
  z-[2]
  flex
  justify-between
  items-center
`

const ToggleSubscriptionButton = observer(() => {
  const [state, store] = useStore()
  const userId = globals.currentUserId
  const onClick = () => userId && store.toggleSubscriber([userId])
  return state.canEditSubscribers !== 'no' ? (
    <LinkButton
      onClick={onClick}
      size="small"
      color="primary"
      className="!text-12"
      noPaddings
    >
      {store.isSubscribed ? Lang.msg.chatUnsubscribe : Lang.msg.chatSubscribe}
    </LinkButton>
  ) : null
})

const SubscribedUsers = observer(() => {
  const [state, store] = useStore()
  return (
    <TwSubscribedUsers
      onClick={store.toggleOpen}
      $clickable={state.canEditSubscribers !== 'no'}
    >
      <TwTitle>{Lang.msg.subscribedToTask}</TwTitle>
      <AvatarGroup
        userIds={state.chosen}
        size={20}
        outlineColor="grey-l80"
        hideStatus
      />
      {state.canEditSubscribers === 'yes' && (
        <IconButton icon="IconCirclePlus" size="small" />
      )}
    </TwSubscribedUsers>
  )
})

const Subscribed = observer(() => {
  return (
    <>
      <TwSubscribed>
        <SubscribedUsers />
        <ToggleSubscriptionButton />
      </TwSubscribed>
      <SubscribersMenu />
    </>
  )
})

const SyncState = React.memo(
  withUpdaters(
    SyncUpdater({filter: '*', debounceTime: 300, companyType: 'current'})
  )(() => {
    const [, store] = useStore()
    useEffect(() => store.syncState())

    return null
  })
)

export const ChatSubscribersChooser = (props: ScopeProps) => {
  const subscribersStore = useMemoOne(
    () =>
      new ChatSubscribersChooserStore(
        new ChatSubscribersService(props.chat?.id),
        props.canEditSubscribers
      ),
    [props.chat?.id, props.canEditSubscribers]
  )

  if (!subscribersStore.hasTask || !subscribersStore.state.canEditSubscribers) {
    return null
  }

  return (
    <StoreContext.Provider value={subscribersStore}>
      <Subscribed />
      <SyncState />
    </StoreContext.Provider>
  )
}
